import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S06P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 06 Parte 1"
      />
      <DefaultSlide title="Agenda">
        <div>
          <ul>
            <li>Examen #1</li>
            <li>JavaScript Parte 3</li>
          </ul>
        </div>
      </DefaultSlide>
      <section>
        <TitleSlide
          title="Examen #1"
          subtitle="Evaluación de los conocimientos adquiridos"
        />
      </section>
      <TitleSlide title="JavaScript" subtitle="Continuación Parte 3" />
      <section>
        <TitleSlide title="Event loop" subtitle="Continuación..." />
        <section>
          <p>
            Hoy terminamos de ver algunos conceptos avanzados de JS. Vamos a
            empezar por repasar el event loop.
          </p>
        </section>
        <DefaultSlide title="Event Loop">
          <div>
            <p>¿Qué es el event loop?</p>
            <StudentPicker number={1} />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Event Loop">
          <div>
            <p>
              JavaScript es un lenguaje sincrónico, lo que quiere decir que no
              puede procesar más de una cosa a la vez.
            </p>
            <p>
              Sin embargo, gracias a la combinación del event loop con la pila
              de llamadas (call stack) y la cola de tareas (task queue), es
              posible manejar operaciones asíncronas como temporizadores,
              eventos y promesas.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <img
            src="/images/S06/eventloopcomponents.png"
            alt="Event loop components representation"
          />
        </section>
        <DefaultSlide title="Componentes del event loop">
          <div>
            <ul>
              <li>Call stack</li>
              <li>Web APIs</li>
              <li>Callback queue</li>
              <li>Microtask queue</li>
              <li>Event loop</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Call stack">
          <div>
            <p>
              La pila de llamadas es donde se ejecutan las funciones. Cada vez
              que se llama a una función, se agrega a la parte superior de la
              pila. Cuando la función termina, se elimina de la pila.
            </p>
            <img
              src="/images/S06/callstack.jpg"
              alt="Callstack representation"
            />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Web APIs">
          <div>
            <p>
              Las Web APIs son funciones que el navegador proporciona para
              manejar tareas asíncronas. Por ejemplo, setTimeout, fetch y
              addEventListener son Web APIs.
            </p>
            <p>
              Cuando se llama a una Web API, se pasa la función de callback a la
              Web API, que la ejecutará cuando la tarea asíncrona se haya
              completado.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Task queue (callback queue)">
          <div>
            <p>
              La cola de tareas es donde se almacenan las funciones de callback
              que se han completado. Cuando la pila de llamadas está vacía, el
              event loop toma la primera función de la cola de tareas y la
              coloca en la pila de llamadas (callstack).
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Microtask queue">
          <div>
            <p>
              La cola de microtareas es similar a la cola de tareas, pero tiene
              una prioridad más alta. Las microtareas se ejecutan antes de que
              se procesen las tareas normales en la cola de tareas.
            </p>
            <p>
              Las microtareas son principalmente promesas y funciones de
              observador de mutaciones.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Event loop">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El event loop es el encargado de mover las funciones de la cola de
              tareas y la cola de microtareas a la pila de llamadas. El event
              loop verifica:
            </p>
            <ol>
              <li>
                Si la pila de llamadas está vacía. Si no está vacía, inicia con
                la ejecución sincrónica.
              </li>
              <li>
                Si lo está, toma la primera función de la cola de microtareas y
                la coloca en la pila de llamadas.
              </li>
              <li>
                Si no hay microtareas, toma la primera función de la cola de
                tareas y la coloca en la pila de llamadas.
              </li>
            </ol>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Veamos un ejemplo y posteriormente esta herramienta de
            visualización.
          </p>
          <a
            href="https://www.jsv9000.app/"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://www.jsv9000.app/
          </a>
        </section>
      </section>
      <section>
        <TitleSlide title="Promesas en JavaScript" />
        <section>
          <p>Veamos un poco más sobre promesas</p>
        </section>
        <DefaultSlide title="Estados de las promesas">
          <div>
            <p>
              Las promesas son objetos que representan el resultado de una
              operación asíncrona. Pueden estar en uno de los siguientes
              estados:
            </p>
            <ul>
              <li>Pending: La promesa está pendiente.</li>
              <li>Fulfilled: La promesa se ha cumplido.</li>
              <li>Rejected: La promesa ha sido rechazada.</li>
            </ul>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creando promesas">
          <div>
            <p>
              Para crear una promesa, usamos el constructor Promise. Este
              constructor toma una función como argumento, que a su vez toma dos
              funciones como argumentos: resolve y reject.
            </p>
            <pre>
              <code className="language-js">
                {`const myPromise = new Promise((resolve, reject) => {
  // Hacemos algo asíncrono
  setTimeout(() => {
    // Si la operación es exitosa
    resolve("Success!");
    // Si la operación falla
    // reject("Error!");
  }, 1000);
});`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Creando promesas">
          <div style={{ fontSize: "0.75em" }}>
            <p>Ejemplo con obtener el valor del dólar de una API.</p>
            <pre>
              <code className="language-js">
                {`const obtainDollarCurrency = new Promise((resolve, reject) => {
  fetch("API").then(() => {
    // Si la operación es exitosa realizar algunas modificaciones
    resolve("Success!");
    // Si la operación falla
    // reject("Error!");
  });
});

// Y luego podemos usar la promesa
obtainDollarCurrency
  .then((result) => {
    console.log(result);
  })
  .catch((error) => {
    console.error(error);
  });`}
              </code>
            </pre>
          </div>
        </DefaultSlide>
        <section>
          <p>¿Qué pasa si no utilizamos Promesas y hacemos algo sincrónico?</p>
        </section>
      </section>
      <section>
        <TitleSlide title="Rendimiento en JavaScript" />
        <section>
          <p>
            Vamos a hablar brevemente de rendimiento, haciendo énfasis en la
            importación de archivos de JS.
          </p>
          <p className="fragment">
            Vimos anteriormente que existen 3 formas de integrar nuestro HTML
            con JavaScript
          </p>
        </section>
        <section>
          <p>1. Usando la etiqueta script en el mismo documento (MALO!)</p>
          <pre>
            <code>
              {`<script>
  console.log("Hello World");
</script>`}
            </code>
          </pre>
        </section>
        <section>
          <p>2. Importando un archivo externo</p>
          <pre>
            <code>{`<script src="script.js"></script>`}</code>
          </pre>
        </section>
        <section>
          <p>3. En el body (MALO!)</p>
          <pre>
            <code>
              {`<div onclick="alert('Hello World')">
  ...
</div>`}
            </code>
          </pre>
        </section>
        <DefaultSlide title="Importación de archivos JS">
          <div>
            <p>
              Cuando importamos un archivo de JS, el navegador tiene que
              descargarlo y ejecutarlo. Esto puede afectar el rendimiento de la
              página.
            </p>
            <p>
              Para mejorar el rendimiento, podemos usar los atributos async y
              defer.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <p>
            <strong>async</strong>: El script se descarga y ejecuta de forma
            asíncrona. Esto significa que no bloquea la carga del HTML de la
            página.
          </p>
          <pre>
            <code>{`<script src="script.js" async></script>`}</code>
          </pre>
        </section>
        <section>
          <p>
            <strong>defer</strong>: El script se descarga de forma asíncrona,
            pero se ejecuta después de que el HTML se haya cargado
            completamente.
          </p>
          <pre>
            <code>{`<script src="script.js" defer></script>`}</code>
          </pre>
        </section>
        <section>
          <p>
            Veamos un ejemplo sobre la importancia de colocar nuestro script al
            final y el uso de async y defer
          </p>
        </section>
        <section>
          <p>
            Hemos cubierto los conceptos más primitivos de JavaScript y ahora
            comprendemos los mecanismos que nuestro motor de JavaScript utiliza
            para proporcionar asincronicidad.
          </p>
          <p>
            Ha llegado la hora de poner todo junto: el motor de html con el
            motor de javascript y aprender sobre el camino crítico de despliegue
            de un sitio web.
          </p>
        </section>
      </section>
      <section>
        <TitleSlide
          title="Camino crítico de despliegue"
          subtitle="The critical rendering path"
        />
        <section>
          <p>
            El camino crítico es la secuencia de pasos que el navegador sigue
            para cargar y mostrar una página web.
          </p>
          <p>
            El camino crítico incluye la descarga de HTML, CSS y JS, así como la
            ejecución de scripts y el renderizado de la página.
          </p>
        </section>
        <DefaultSlide title="Camino crítico">
          <div>
            <img
              src="/images/S06/renderpath.webp"
              alt="Critical path representation"
            />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Camino crítico">
          <div>
            <p>
              El camino crítico se puede dividir en tres etapas:{" "}
              <strong>Construcción del DOM</strong>,{" "}
              <strong>Construcción del CSSOM</strong> y{" "}
              <strong>Renderizado</strong>.
            </p>
            <p>
              Formalmente existen otros pasos en el proceso como Layout,
              painting, compositing, entre otros. Veremos más sobre esto al ver
              el tema de performance en profundidad.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Construcción del DOM">
          <div>
            <p>
              La construcción del DOM es el proceso de convertir el HTML en un
              árbol de nodos. Cada nodo representa un elemento del HTML.
            </p>
            <img
              src="/images/S06/dom.png"
              width={300}
              alt="DOM representation"
            />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Construcción del CSSOM">
          <div>
            <p>
              La construcción del CSSOM es el proceso de convertir el CSS en un
              árbol de nodos. Cada nodo representa una regla CSS.
            </p>
          </div>
        </DefaultSlide>
        <DefaultSlide title="Renderizado">
          <div>
            <p>
              El renderizado es el proceso de combinar el DOM y el CSSOM en un
              solo árbol, llamado render tree. Este árbol se utiliza para
              dibujar la página en la pantalla.
            </p>
          </div>
        </DefaultSlide>
        <section>
          <img src="/images/S06/domvscssom.png" alt="DOM vs CSSOM" />
        </section>
        <DefaultSlide title="Renderizado">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El renderizado es el proceso de convertir el árbol de renderizado
              en píxeles en la pantalla. Esto incluye el cálculo de la posición
              y el tamaño de cada elemento, así como la aplicación de estilos y
              la pintura de los píxeles.
            </p>
            <img
              src="/images/S06/render.png"
              alt="Render tree representation"
              width={300}
              style={{ margin: "0 auto" }}
            />
          </div>
        </DefaultSlide>
        <DefaultSlide title="Renderizado">
          <div style={{ fontSize: "0.75em" }}>
            <p>
              El renderizado es un proceso costoso, por lo que es importante
              optimizarlo. Algunas formas de optimizar el renderizado son:
            </p>
            <ul>
              <li>
                Usar técnicas de tree shaking (mover el árbol para que se caigan
                las ramas que no sirven)
              </li>
              <li>Usar técnicas de minificación</li>
              <li>Usar técnicas de compresión</li>
              <li>Usar técnicas de caché</li>
              <li className="fragment">
                <strong>
                  Controlar nosotros mismos la creación del DOM para que el
                  renderizado se haga de forma óptima?
                </strong>
              </li>
            </ul>
          </div>
        </DefaultSlide>
        <section>
          <p>
            Con esa idea vamos a finalizar el tema de rendimiento, para
            introducir la técnica llamada Virtual DOM, utilizada por React
          </p>
          <p>
            Pero antes debemos aprender sobre NodeJS como herramienta de
            desarrollo y posteriormente sobre herramientas boilerplate para
            crear proyectos de todo tipo.
          </p>
        </section>
      </section>
      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de parte 1" />
    </>
  );
}
